/**
 * FilterRow — a single filter condition row in the Bulk Add filter panel.
 *
 * Each row is a 3-column form: [Field] [Operator] [Value] + a delete button.
 *
 * This file is responsible for:
 *   - Populating the field dropdown from CDG header metadata (TEXT,
 *     NUMBER, DATE, BOOLEAN fields only — other types are excluded
 *     because the UI has no meaningful input control for them).
 *   - **Type-aware operators** — when the user picks a field, the
 *     component looks up that field's `type` in the metadata and swaps
 *     the operator dropdown to show only semantically valid choices.
 *     For example, DATE fields offer "Before / After" while TEXT fields
 *     offer "Contains / Starts With".  This prevents nonsensical
 *     filter combinations (e.g. "Contains" on a date).
 *   - **Cascading reset** — changing the field clears both the operator
 *     and value inputs so the user is never left with an invalid
 *     combination from a previous selection.
 *   - Rendering the correct value input widget (date picker vs text
 *     field vs number field) via FilterValueInput.
 */
import React, { useEffect, useState } from 'react'
import { Divider, Grid } from '@mui/material'
import FormAutoComplete from '../../../../../components/FormElements/FormAutoComplete'
import FilterValueInput from './FilterValueInput'
import GenericButton from '../../../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../../../components/GenericButton/styles'
import { ReactComponent as DeleteIcon } from '../../../../../assets/trash.svg'
import { OPERATORS_BY_TYPE } from '../../../../../constants/filters'

interface FilterRowProps {
  item: Partial<any>
  index: number
  methods: any
  handleRemoveFilter: (index: number) => void
  /** CDG header metadata — filterable fields with their data types. */
  filterRows: any[]
  filterRowsLoading: boolean
  filterRowsError: any
}

const FilterRow: React.FC<FilterRowProps> = ({ item, index, methods, handleRemoveFilter, filterRows, filterRowsLoading, filterRowsError }) => {
  // selectedType drives the value input widget (DATE → date picker,
  // NUMBER → numeric input, TEXT → text input).
  const [selectedType, setSelectedType] = useState('DATE')
  // availableOperators is the subset of OPERATORS_BY_TYPE that matches
  // the currently selected field's data type.
  const [availableOperators, setAvailableOperators] = useState(OPERATORS_BY_TYPE.DATE)

  // Only expose field types we have UI controls for.  Other CDG types
  // (e.g. ARRAY, OBJECT) are excluded because no sensible operator
  // or input widget exists for them in this filter context.
  const FilteredRow = filterRows.filter(rowData => rowData?.type === 'TEXT' || rowData?.type === 'NUMBER' || rowData?.type === 'DATE' || rowData?.type === 'BOOLEAN')

  // Reacts to the user selecting a different field.  Looks up the
  // field's type in the CDG metadata and updates both the operator
  // dropdown contents and the value input type.  Falls back to TEXT
  // operators if the type is unrecognised.
  useEffect(() => {
    const selectedCategory = methods.getValues?.(`filters[${index}].category`)
    const selectedField = FilteredRow.find(row => row.field === selectedCategory)
    if (selectedField?.type) {
      setSelectedType(selectedField.type)
      setAvailableOperators(OPERATORS_BY_TYPE[selectedField.type] || OPERATORS_BY_TYPE.TEXT)
    } else {
      setSelectedType('DATE')
      setAvailableOperators(OPERATORS_BY_TYPE.DATE)
    }
  }, [methods, index, FilteredRow, methods.watch(`filters[${index}].category`)])

  return (
    <>
      {index > 0 && (
        <Divider variant='middle' textAlign="left" sx={{ width: '90%', my: 4 }}>
          and
        </Divider>
      )}
      <Grid key={item.id} container spacing={4} justifyContent="space-between" alignItems="center">
        <Grid item xs={12} sm={3} md={4}>
          <FormAutoComplete
            control={methods.control}
            options={FilteredRow}
            placeholder="End Date"
            name={`filters[${index}].category`}
            getOptionLabel={(option: any) => option?.headerName}
            getOptionValue={(option: any) => option?.field}
            onChange={val => {
              const prevCategory = methods.getValues(`filters[${index}].category`)
              const prevType = FilteredRow.find(row => row.field === prevCategory)?.type
              const newType = FilteredRow.find(row => row.field === val?.field)?.type
              methods.setValue(`filters[${index}].category`, val ? val.field : '')
              // Only reset operator and value when the field type changes (e.g. DATE → TEXT).
              // Preserving them when the type stays the same lets the user switch between
              // fields of the same type without having to re-fill the condition.
              if (prevType !== newType) {
                methods.setValue(`filters[${index}].operator`, '')
                methods.setValue(`filters[${index}].date`, '')
              }
            }}
            wrapperStyle={{ minHeight: 'auto' }}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={3}>
          <FormAutoComplete
            control={methods.control}
            options={availableOperators}
            placeholder="Select"
            name={`filters[${index}].operator`}
            getOptionLabel={(option: any) => option?.title}
            getOptionValue={(option: any) => option?.value}
            onChange={val => {
              // Store the API operator symbol (e.g. "==", ">=", "<") not the UI label.
              methods.setValue(`filters[${index}].operator`, val ? val.value : '')
            }}
            fullWidth
            wrapperStyle={{ minHeight: 'auto' }}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={4}>
          <FilterValueInput
            type={selectedType as 'DATE' | 'NUMBER' | 'TEXT'}
            name={`filters[${index}].date`}
            placeholder={selectedType === 'DATE' ? '08/25/2025' : selectedType === 'NUMBER' ? 'Enter number' : 'Enter text'}
            register={methods.register}
          />
        </Grid>
        <Grid item xs={12} sm={1} md={1}>
          <GenericButton
            icon={<DeleteIcon />}
            buttonSize={ButtonSize.Medium}
            buttonType={ButtonType.Ghost}
            style={{ background: 'transparent' }}
            onClick={() => { handleRemoveFilter(index) }}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default FilterRow
